import { fileToBase64, splitImageSrc } from "@/common/helpers";
import {
	EmailData,
	AttachmentData as EmailEditorAttachmentData,
} from "@/components/molecules/emailEditor";
import {
	AttachmentData,
	SendAttachmentData,
} from "@/repository/models/message";
import { useCallback, useMemo } from "react";

export interface InlineImages {
	originalSrc: string;
	attachment: SendAttachmentData;
}

export const useEmailInteraction = () => {
	const extractDataCid = (imageHtml: string) => {
		const pattern = /data-cid=["']([^"']*)/;
		const match = imageHtml.match(pattern);
		if (match) {
			return match[1];
		}

		return null;
	};

	const getInlineImages = useCallback(async (body: string) => {
		const tmpHtml = document.createElement("div");
		tmpHtml.innerHTML = body;

		const htmlImages = tmpHtml.querySelectorAll("img");
		const images: any[] = [];
		htmlImages.forEach((x) =>
			images.push({
				elementAsString: x.outerHTML,
				src: x.getAttribute("src"),
			})
		);

		const attachments = await getImagesAsAttachments(images);

		const imagesArray = [
			...attachments
				.reduce((p, n) => {
					return p.set(n.key, n);
				}, new Map<string, InlineImages>())
				.values(),
		];

		return imagesArray;
	}, []);

	const isValidHttpUrl = (input: any) => {
		let url;

		try {
			url = new URL(input);
		} catch (err) {
			console.log(err);
			return false;
		}

		return url.protocol === "http:" || url.protocol === "https:";
	};

	const getImagesAsAttachments = async (images: any) => {
		const attachments: any[] = [];
		await images.forEach(async (image: any) => {
			let [type, content] = splitImageSrc(image.src);

			const id =
				extractDataCid(image.elementAsString) ?? crypto.randomUUID().toString();

			if (isValidHttpUrl(image.src)) {
				 return;
			}

			attachments.push({
				originalSrc: image.src,
				attachment: {
					name: id,
					contentType: type,
					contentId: id,
					isInline: true,
					contentBytes: content,
					toRemove: false,
				},
				key: id,
			});
		});

		return attachments;
	};

	const replaceInlineImages = useCallback(
		(body: string, attachments: InlineImages[]) => {
			return attachments.reduce((p, n) => {
				return p.replace(n.originalSrc, `cid:${n.attachment.contentId}`);
			}, body);
		},
		[]
	);

	const replaceContentIdWithSource = useCallback(
		(body: string, attachments: AttachmentData[]) =>
			attachments.reduce(
				(p, n) =>
					p.replace(
						`src="cid:${n.contentId}"`,
						`src="data:${n.contentType};base64,${n.contentBytes}" data-cid="${n.contentId}"`
					),
				body
			),
		[]
	);

	const processInlineImages = async (
		mailData: EmailData
	): Promise<{ formattedBody: string }> => {
		const inlineImages = await getInlineImages(mailData.body);
		const formattedBody = replaceInlineImages(mailData.body, inlineImages);

		return { formattedBody };
	};

	const prepareDraftAttachments = async (
		mailData: EmailData
	): Promise<{
		attachmentsToUpload: SendAttachmentData[];
		attachmentsToRemove: EmailEditorAttachmentData[];
	}> => {
		const inlineImages = await getInlineImages(mailData.body);

		const toUpload: SendAttachmentData[] = inlineImages
			.filter(
				(x) =>
					!mailData.attachments.some(
						(y) => y.contentId == x.attachment.contentId
					)
			)
			.map((x) => x.attachment);

		const toRemove = mailData.attachments.filter(
			(x) =>
				x.contentId &&
				!inlineImages.some((f) => f.attachment.contentId === x.contentId)
		);

		return { attachmentsToUpload: toUpload, attachmentsToRemove: toRemove };
	};

	const prepareAttachments = async (
		mailData: EmailData
	): Promise<{ attachments: SendAttachmentData[]; formattedBody: string }> => {
		let attachments: SendAttachmentData[] = await Promise.all(
			mailData.attachments.map(async (x: EmailEditorAttachmentData) => ({
				id: x.id,
				name: x.file.name,
				contentType: x.file.type,
				contentId: x.contentId,
				isInline: !!x.contentId,
				contentBytes: await fileToBase64(x.file),
				toRemove: false,
			}))
		);

		const inlineImages = await getInlineImages(mailData.body);
		const formattedBody = replaceInlineImages(mailData.body, inlineImages);

		attachments = getAttachmentsList(
			inlineImages,
			attachments,
			mailData.refMessage?.attachments
		);

		return { attachments, formattedBody };
	};

	const getAttachmentsList = (
		inlineImages: InlineImages[],
		attachments: SendAttachmentData[],
		uiAttachments: AttachmentData[] | null | undefined
	): SendAttachmentData[] => {
		if (uiAttachments?.length) {
			const toRemove = uiAttachments
				.filter((x) => !attachments.some((f) => f.id === x.id))
				.map((x) => ({
					id: x.id,
					name: x.name,
					contentType: x.contentType,
					contentId: x.contentId,
					isInline: x.isInline,
					contentBytes: "",
					toRemove: true,
				}));

			toRemove.forEach((x) => {
				const skipRemoving = inlineImages.filter(
					(y) => y.attachment.contentId === x.contentId
				).length;

				if (!skipRemoving) {
					attachments = attachments.filter((z) => z.contentId !== x.contentId);
					attachments.push(x);
				}
			});
		}

		inlineImages.forEach((x) => {
			const skippAdding = attachments.filter(
				(y) => y.contentId === x.attachment.contentId
			).length;

			if (!skippAdding) {
				attachments.push(x.attachment);
			}
		});

		return attachments;
	};

	return useMemo(
		() => ({
			replaceContentIdWithSource,
			prepareAttachments,
			processInlineImages,
			prepareDraftAttachments,
		}),
		[]
	);
};
